import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  Toolbar,
  Box,
  LinearProgress,
  TextField,
  InputAdornment,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Paper,
  useMediaQuery,
} from '@mui/material';
import ViewerDialogContent from './ViewerDialogContent';
import ViewerDialogFrame from './ViewerDialogFrame';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Code as CodeIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  FormatIndentIncrease as FormatIndentIncreaseIcon,
  DataObject as DataObjectIcon,
  DataArray as DataArrayIcon,
  Block as BlockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useViewerData } from './viewerHttp';

// Theme colors
const primaryColor = '#5757b0';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: '90vw',
    height: '90vh',
    backgroundColor: '#f5f5f5',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3), 0 8px 32px rgba(87, 87, 176, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  '& .MuiDialog-paper.MuiDialog-paperFullScreen': {
    width: '100vw',
    height: '100vh',
    maxWidth: '100vw',
    maxHeight: '100vh',
    borderRadius: 0,
    margin: 0,
  },
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialog-paper': {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      borderRadius: 0,
      margin: 0,
    },
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(87, 87, 176, 0.15)',
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${primaryColor} 0%, #6b6bce 50%, #8080d7 100%)`,
  color: 'white',
  padding: '12px 20px',
  paddingTop: 'calc(12px + env(safe-area-inset-top))',
  paddingLeft: 'calc(20px + env(safe-area-inset-left))',
  paddingRight: 'calc(20px + env(safe-area-inset-right))',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '1.1rem',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(87, 87, 176, 0.25)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  '& .MuiTypography-root': {
    flex: 1,
    margin: 0,
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.025em',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  },
  '& > .MuiBox-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
  '& > .MuiBox-root > .MuiTypography-root': {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& > .MuiIconButton-root': {
    flexShrink: 0,
  },
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
    paddingTop: 'calc(10px + env(safe-area-inset-top))',
    paddingLeft: 'calc(12px + env(safe-area-inset-left))',
    paddingRight: 'calc(12px + env(safe-area-inset-right))',
    fontSize: '1rem',
    flexWrap: 'nowrap',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: '1rem',
      minWidth: 0,
      wordBreak: 'break-word',
    },
  },
}));

const ToolbarContainer = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  minHeight: '48px !important',
  padding: '0 20px',
  justifyContent: 'space-between',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down('sm')]: {
    padding: '0 12px',
    minHeight: '44px !important',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'flex-start',
  },
}));

const JsonContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  backgroundColor: '#f8f9fa',
  overflow: 'hidden',
}));

const JsonPanel = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const StatsCard = styled(Card)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '6px',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  margin: '8px 12px 0 12px',
  [theme.breakpoints.down('sm')]: {
    margin: '6px 8px 0 8px',
  },
}));

const FilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '16px 20px',
  backgroundColor: 'white',
  borderBottom: '1px solid rgba(87, 87, 176, 0.1)',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
    gap: '12px',
  },
}));

const JsonViewer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: '20px',
  backgroundColor: '#f8f9fa',
}));

const JsonContent = styled(Paper)(({ theme }) => ({
  padding: '20px',
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(87, 87, 176, 0.1)',
  fontFamily: 'monospace',
  fontSize: '14px',
  lineHeight: '1.5',
  overflow: 'auto',
  minHeight: '300px',
}));

const JsonKey = styled('span')(({ theme }) => ({
  color: '#0066cc',
  fontWeight: 'bold',
}));

const JsonString = styled('span')(({ theme }) => ({
  color: '#d73a49',
}));

const JsonNumber = styled('span')(({ theme }) => ({
  color: '#005cc5',
}));

const JsonBoolean = styled('span')(({ theme }) => ({
  color: '#e36209',
}));

const JsonNull = styled('span')(({ theme }) => ({
  color: '#6f42c1',
}));

const JsonBracket = styled('span')(({ theme }) => ({
  color: '#24292e',
  fontWeight: 'bold',
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  padding: '2px',
  marginLeft: '8px',
  fontSize: '12px',
}));

const JSONViewer = ({ open, onClose, filename = '', eventId = null, filePath = '', embedded = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    data: rawJsonData,
    loading,
    error,
  } = useViewerData({
    open,
    eventId,
    filePath,
    missingMessage: t('viewer.json_missing_source', 'File id or path is required'),
  });
  const jsonData = useMemo(() => {
    if (rawJsonData == null) return null;
    if (typeof rawJsonData !== 'string') return rawJsonData;
    try {
      return JSON.parse(rawJsonData);
    } catch {
      return rawJsonData;
    }
  }, [rawJsonData]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('formatted');
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  const [showStats, setShowStats] = useState(true);
  const containerRef = useRef(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setExpandedPaths(new Set());
    }
  }, [open, eventId, filePath]);

  const downloadJson = () => {
    if (jsonData) {
      const content = JSON.stringify(jsonData, null, 2);
      const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename || 'data'}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const copyToClipboard = () => {
    if (jsonData) {
      const content = JSON.stringify(jsonData, null, 2);
      navigator.clipboard.writeText(content);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (containerRef.current && containerRef.current.requestFullscreen) {
        containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setIsFullscreen(false);
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Analyze JSON structure
  const analyzeJson = (obj, path = '') => {
    let stats = {
      objects: 0,
      arrays: 0,
      strings: 0,
      numbers: 0,
      booleans: 0,
      nulls: 0,
      totalKeys: 0,
      maxDepth: 0,
      paths: []
    };

    const analyze = (value, currentPath, depth) => {
      stats.maxDepth = Math.max(stats.maxDepth, depth);

      if (value === null) {
        stats.nulls++;
      } else if (typeof value === 'boolean') {
        stats.booleans++;
      } else if (typeof value === 'number') {
        stats.numbers++;
      } else if (typeof value === 'string') {
        stats.strings++;
      } else if (Array.isArray(value)) {
        stats.arrays++;
        stats.paths.push({ path: currentPath, type: 'array', length: value.length });
        value.forEach((item, index) => {
          analyze(item, `${currentPath}[${index}]`, depth + 1);
        });
      } else if (typeof value === 'object') {
        stats.objects++;
        const keys = Object.keys(value);
        stats.totalKeys += keys.length;
        stats.paths.push({ path: currentPath, type: 'object', keys: keys.length });
        keys.forEach(key => {
          analyze(value[key], currentPath ? `${currentPath}.${key}` : key, depth + 1);
        });
      }
    };

    analyze(obj, path, 0);
    return stats;
  };

  const stats = useMemo(() => (jsonData ? analyzeJson(jsonData) : null), [jsonData]);

  // Format JSON for display
  const formatValue = (value, path = '', depth = 0) => {
    if (value === null) {
      return <JsonNull>null</JsonNull>;
    }

    if (typeof value === 'boolean') {
      return <JsonBoolean>{value.toString()}</JsonBoolean>;
    }

    if (typeof value === 'number') {
      return <JsonNumber>{value}</JsonNumber>;
    }

    if (typeof value === 'string') {
      const highlighted = searchTerm && value.toLowerCase().includes(searchTerm.toLowerCase())
        ? value.replace(new RegExp(`(${searchTerm})`, 'gi'), '<mark>$1</mark>')
        : value;
      return <JsonString>"{highlighted}"</JsonString>;
    }

    if (Array.isArray(value)) {
      const isExpanded = expandedPaths.has(path);
      return (
        <div>
          <JsonBracket>[</JsonBracket>
          <ExpandButton
            size="small"
            onClick={() => toggleExpanded(path)}
          >
            {isExpanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </ExpandButton>
          {isExpanded && (
            <div style={{ marginLeft: '20px' }}>
              {value.map((item, index) => (
                <div key={index} style={{ marginBottom: '4px' }}>
                  <JsonKey>{index}:</JsonKey> {formatValue(item, `${path}[${index}]`, depth + 1)}
                  {index < value.length - 1 && ','}
                </div>
              ))}
            </div>
          )}
          <JsonBracket>]</JsonBracket>
        </div>
      );
    }

    if (typeof value === 'object') {
      const isExpanded = expandedPaths.has(path);
      const keys = Object.keys(value);
      return (
        <div>
          <JsonBracket>{'{'}</JsonBracket>
          <ExpandButton
            size="small"
            onClick={() => toggleExpanded(path)}
          >
            {isExpanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </ExpandButton>
          {isExpanded && (
            <div style={{ marginLeft: '20px' }}>
              {keys.map((key, index) => (
                <div key={key} style={{ marginBottom: '4px' }}>
                  <JsonKey>"{key}":</JsonKey> {formatValue(value[key], path ? `${path}.${key}` : key, depth + 1)}
                  {index < keys.length - 1 && ','}
                </div>
              ))}
            </div>
          )}
          <JsonBracket>{'}'}</JsonBracket>
        </div>
      );
    }

    return String(value);
  };

  const toggleExpanded = (path) => {
    const newExpanded = new Set(expandedPaths);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedPaths(newExpanded);
  };

  const expandAll = useCallback(() => {
    if (stats) {
      const allPaths = new Set(stats.paths.map(p => p.path));
      setExpandedPaths(allPaths);
    }
  }, [stats]);

  const collapseAll = () => {
    setExpandedPaths(new Set());
  };

  useEffect(() => {
    if (stats) {
      expandAll();
    } else {
      setExpandedPaths(new Set());
    }
  }, [stats, expandAll]);

  const getFormattedJson = () => {
    if (!jsonData) return '';
    
    if (viewMode === 'raw') {
      return JSON.stringify(jsonData, null, 2);
    }
    
    return formatValue(jsonData);
  };

  return (
    <ViewerDialogFrame
      embedded={embedded}
      DialogComponent={StyledDialog}
      open={open} 
      onClose={onClose}
      maxWidth={false}
      fullScreen={isFullscreen || isSmallScreen}
    >
      <StyledDialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <CodeIcon sx={{ 
            mr: 1.5, 
            fontSize: '1.4rem',
            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
          }} />
          <Typography variant="h6" component="div" sx={{ margin: 0 }}>
            {filename ? `${t('viewer.json_viewer_title')} - ${filename}` : t('viewer.json_viewer_title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ 
            color: 'white',
            marginLeft: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            transition: 'all 0.3s ease',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <ToolbarContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton 
            onClick={downloadJson} 
            disabled={!jsonData}
            size="small"
            title={t('viewer.download_json')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={copyToClipboard} 
            disabled={!jsonData}
            size="small"
            title="Copy to Clipboard"
          >
            <ContentCopyIcon />
          </IconButton>
          <IconButton 
            onClick={toggleFullscreen}
            size="small"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Box>
        
        {stats && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={`${stats.objects} objects`}
              size="small"
              variant="outlined"
              icon={<DataObjectIcon />}
            />
            <Chip 
              label={`${stats.arrays} arrays`}
              size="small"
              variant="outlined"
              icon={<DataArrayIcon />}
            />
            <Chip 
              label={`Depth: ${stats.maxDepth}`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </ToolbarContainer>

      <ViewerDialogContent sx={{ "--dialog-content-top-padding": "0px", padding: 0, height: '100%', overflow: 'hidden' }}>
        <JsonContainer ref={containerRef}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              gap: 2
            }}>
              <LinearProgress sx={{ width: 300 }} />
              <Typography color="text.secondary">
                {t('viewer.loading_json')}
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="error" variant="h6" gutterBottom>
                {t('viewer.error_loading_json')}
              </Typography>
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          ) : jsonData ? (
            <JsonPanel>
              {/* Statistics Card */}
              {showStats && stats && (
                <StatsCard>
                  <CardContent sx={{ py: 0.75, px: 1.5, '&:last-child': { pb: 0.75 } }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5, lineHeight: 1.2 }}>
                      JSON Structure Analysis
                    </Typography>
                    <Grid container spacing={0.5}>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle1" color="primary" sx={{ lineHeight: 1.15 }}>
                            {stats.objects}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Objects
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle1" color="primary" sx={{ lineHeight: 1.15 }}>
                            {stats.arrays}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Arrays
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle1" color="primary" sx={{ lineHeight: 1.15 }}>
                            {stats.strings}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Strings
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="subtitle1" color="primary" sx={{ lineHeight: 1.15 }}>
                            {stats.maxDepth}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Max Depth
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </StatsCard>
              )}

              {/* Filter Row */}
              <FilterRow>
                <TextField
                  size="small"
                  placeholder={t('viewer.search_json_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={clearSearch}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 200 }}
                />
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>View Mode</InputLabel>
                  <Select
                    value={viewMode}
                    onChange={(e) => setViewMode(e.target.value)}
                    label="View Mode"
                  >
                    <MenuItem value="formatted">Formatted</MenuItem>
                    <MenuItem value="raw">Raw</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  startIcon={<FormatIndentIncreaseIcon />}
                  variant="outlined"
                  size="small"
                  onClick={expandAll}
                >
                  Expand All
                </Button>
                
                <Button
                  startIcon={<BlockIcon />}
                  variant="outlined"
                  size="small"
                  onClick={collapseAll}
                >
                  Collapse All
                </Button>
                
                <Button
                  startIcon={showStats ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => setShowStats(!showStats)}
                >
                  {showStats ? 'Hide' : 'Show'} Stats
                </Button>
              </FilterRow>

              {/* JSON Viewer */}
              <JsonViewer>
                <JsonContent>
                  {viewMode === 'raw' ? (
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(jsonData, null, 2)}
                    </pre>
                  ) : (
                    <div>
                      {getFormattedJson()}
                    </div>
                  )}
                </JsonContent>
              </JsonViewer>
            </JsonPanel>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              padding: 4
            }}>
              <Typography color="text.secondary">
                {t('viewer.no_json_content')}
              </Typography>
            </Box>
          )}
        </JsonContainer>
      </ViewerDialogContent>
    </ViewerDialogFrame>
  );
};

export default JSONViewer;
